import type { ItemId } from '../../shared/items.ts';
import { itemCatalog, itemVisual } from '../common/item-catalog.ts';
import { elementTypes, matchupsAgainst, typesOf, type Element } from '../../shared/elements.ts';
import { ENERGY_MAX, STATUSES, type BattleStat, type MoveAnimation, type StatusCondition } from '../../shared/moves.ts';
import { NO_WEATHER, type Weather } from '../../shared/weather.ts';
import { gameConfig } from '../../shared/game-config.ts';
import { animationCatalog, drawAnimationFrame, effectSheet } from '../common/battle-animations.ts';
import { MAX_FIELD_PLACES, type BattleSide, type BattleSlot, type OwnedMonsterView, type TeamCount, type WildMonsterView } from '../../shared/protocol.ts';
import { TEAM_SIZE } from '../../shared/game-rules.ts';
import type { IconName } from '../../shared/icons.ts';
import { badgeIcon, icon } from '../common/icons.ts';
import { TypedText } from './text-effects.ts';
import { $, el, restartAnimation } from '../common/dom.ts';
import { applyElementBadge } from '../common/element-badge.ts';
import { setHpBar } from './monster-card.ts';
import type { Pacer } from './pacer.ts';
import type { SoundBoard } from './sound.ts';
import { paintSpriteCanvas } from '../common/monster-sprite.ts';

/** Durées de la mise en scène, en millisecondes (divisées en mode accéléré). */
export const TIMING = {
  typePerChar: 6,
  afterLine: 650,
  enter: 650,
  hit: 500,
  /** Arrêt sur image au moment du coup (plus long sur un critique ou un coup super efficace). */
  hitStop: 70,
  hitStopStrong: 140,
  /** K.O. : la cible blanchit un instant avant de tomber, au ralenti. */
  faintFlash: 260,
  /** Coup de zoom, bref, juste avant une grosse attaque. */
  cinematic: 220,
  /** Annonce d'un statut infligé. */
  afflict: 750,
  /** Arrêt sur image d'un coup critique, zoomé sur la cible. */
  hitStopCritical: 220,
  /** L'adversaire apparaît en silhouette, puis se colore. */
  reveal: 450,
  dodge: 450,
  rest: 900,
  /** Pièces qui s'envolent vers le compteur. */
  coins: 900,
  /** Durée maximale du remplissage de la barre d'expérience, et intervalle de ses tics. */
  xpFill: 1100,
  xpTick: 70,
  /** La barre d'expérience reste affichée ce temps-là après le dernier gain, puis s'efface. */
  xpLinger: 1200,
  hpDrain: 700,
  faint: 900,
  attack: 380,
  capsuleThrow: 800,
  capsuleOpen: 450,
  capsuleDrop: 550,
  capsuleShake: 850,
  capsuleResult: 950,
} as const;

/** Secousse de l'arène au coup, en pixels : de base, selon la part des PV max perdue, et en plus sur un coup fort. */
const SHAKE = { base: 2, perMaxHp: 14, strong: 3, max: 14 } as const;

/** Puissance à partir de laquelle une attaque assombrit l'arène à sa couleur, et à partir de laquelle elle a droit au zoom. */
const TINT_POWER = 80;
const CINEMATIC_POWER = 100;
/** Part des PV max perdue (ou coup fort) à partir de laquelle la cible est projetée en arrière. */
const KNOCKBACK_LOSS = 0.25;

/** Particules qui restent dans l'arène après une attaque, selon son type (les types ajoutés dans l'admin : poussière). */
type ParticleKind = 'ember' | 'flake' | 'drop' | 'leaf' | 'spark' | 'pebble' | 'wisp' | 'twinkle' | 'bubble' | 'streak' | 'dust';
const PARTICLES: Readonly<Record<string, ParticleKind>> = {
  fire: 'ember', ice: 'flake', water: 'drop', grass: 'leaf', electric: 'spark', rock: 'pebble',
  ghost: 'wisp', astral: 'twinkle', toxic: 'bubble', air: 'streak', normal: 'dust', fighting: 'dust',
};

/** Sous cette part des PV max, votre monstre est en danger : jauge qui clignote, bords rouges, battements de cœur. */
const DANGER_HP = 0.2;
const HEARTBEAT_MS = 1100;
/** Crans de statistique affichés à côté du nom. */
const STAGE_LABELS: Readonly<Record<BattleStat, string>> = { atk: 'Atq', def: 'Déf', spd: 'Vit', accuracy: 'Préc', evasion: 'Esq' };

/** Ce qui arrive à l'attaque : elle touche, la cible l'esquive, ou elle n'a aucun effet. */
export type AttackOutcome = 'hit' | 'dodge' | 'immune';

/** Vibration du téléphone (si l'appareil le permet et que l'on n'a pas demandé moins d'animations). */
function vibrate(pattern: number | number[]): void {
  if (!('vibrate' in navigator) || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  navigator.vibrate(pattern);
}

const MAX_LOG_LINES = 3;
/** Étincelles de la capture réussie. */
const SPARKS = 10;

type Displayed = WildMonsterView | OwnedMonsterView;

/** Une place de l'arène : le camp (vu par le joueur) et la place (0, ou 1 en combat double). */
export type Slot = BattleSlot;
const SIDES: readonly BattleSide[] = ['ally', 'foe'];
/** Toutes les places de l'arène, affichées ou non. */
const ALL_SLOTS: readonly Slot[] = SIDES.flatMap((target) => Array.from({ length: MAX_FIELD_PLACES }, (_, place) => ({ target, place })));
const slotKey = ({ target, place }: Slot): string => `${target}${place}`;
/** Le camp d'en face. */
const opposite = (side: BattleSide): BattleSide => (side === 'ally' ? 'foe' : 'ally');

/** Écran de combat caché : délai entre deux vérifications avant de reprendre le dessin des monstres. */
const HIDDEN_REPAINT_MS = 250;

/**
 * Taille d'affichage des animations d'attaque et de statut, proportionnelle à celle de leurs images : une image de
 * EFFECT_BASE.size pixels couvre EFFECT_BASE.percent % de la largeur du monstre (une de 256 px, environ 3,5 fois).
 * Les animations d'attaque sont ensuite réduites de ATTACK_EFFECT_SCALE ; celles de statut, dessinées autour du
 * monstre, gardent leur taille pour ne pas lui marcher dessus.
 */
const EFFECT_BASE = { size: 96, percent: 130 } as const;
const ATTACK_EFFECT_SCALE = 0.65;
const effectWidth = (width: number, scale = 1): string => `${(EFFECT_BASE.percent * width * scale) / EFFECT_BASE.size}%`;

/** Petite capsule à côté du nom d'un adversaire dont le joueur a déjà capturé l'espèce. */
function caughtMark(): HTMLElement {
  const mark = itemVisual(itemCatalog.get('capsule'), 'caught-mark');
  mark.title = 'Espèce déjà capturée';
  mark.setAttribute('aria-label', mark.title);
  mark.setAttribute('role', 'img');
  return mark;
}

/** Joue l'animation CSS de cette classe une fois, puis la retire (pour ne pas masquer celles du monstre ensuite). */
function playOnce(element: HTMLElement, className: string): void {
  restartAnimation(element, className);
  element.addEventListener('animationend', function done(event) {
    if (event.target !== element || !event.animationName) return;
    element.classList.remove(className);
    element.removeEventListener('animationend', done);
  });
}

/**
 * Mise en scène du combat : remplit l'arène et joue chaque animation en attendant qu'elle soit
 * finie, pour que le joueur ait le temps de comprendre ce qui se passe.
 */
export class BattleStage {
  private xpHideTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly root: HTMLElement;
  private readonly arena: HTMLElement;
  private readonly log: HTMLElement;
  private readonly capsule: HTMLElement;
  /** Voile de la couleur des grosses attaques. */
  private readonly tint: HTMLElement;
  /** Lueur de la couleur du type renforcé par la météo, sur les bords de l'écran. */
  private readonly glow: HTMLElement;
  /** Éclair blanc des coups forts et des K.O. */
  private readonly flashVeil: HTMLElement;
  private weather: Weather = NO_WEATHER;
  /** Crans de statistique de chaque monstre en jeu, par place (remis à zéro quand un monstre entre). */
  private readonly stages = new Map<string, Partial<Record<BattleStat, number>>>();
  /** Battements de cœur quand votre monstre est en danger. */
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private readonly sounds: SoundBoard;
  private readonly pacer: Pacer;
  /** Monstre affiché à chaque place. */
  private readonly shown = new Map<string, Displayed>();
  /** PV affichés à chaque place (la secousse dépend des PV perdus). */
  private readonly hp = new Map<string, number>();
  /** Pendant un arrêt sur image : tout ce qui bouge attend qu'il se termine. */
  private frozen: Promise<void> | null = null;
  /** Statut à chaque place, dont l'animation (onglet Configuration) tourne en boucle sur le monstre. */
  private readonly statuses = new Map<string, StatusCondition | null>();
  /** Places utilisées de chaque camp (2 : combat double). */
  private placeCount: Record<BattleSide, number> = { ally: 1, foe: 1 };
  /** Les monstres affichés sont redessinés à chaque image (animations), tant que l'arène est visible. */
  private painting = false;
  /** Capsules de l'équipe adverse, en haut à droite (dresseur ou duel). */
  private readonly foeTeamRow = el('div', { className: 'foe-team', hidden: true });
  private foeTeam: TeamCount | null = null;

  constructor(root: HTMLElement, sounds: SoundBoard, pacer: Pacer) {
    this.root = root;
    this.arena = $('.arena', root);
    this.log = $('.log', root);
    this.capsule = $('.capsule', root);
    this.sounds = sounds;
    this.pacer = pacer;
    // Le combat se joue sur la carte (voir BattleField) : halo autour de l'arène et voile coloré derrière les monstres,
    // lueur et flash devant, sous le panneau du bas.
    this.tint = el('div', { className: 'battle-tint' });
    this.glow = el('div', { className: 'battle-glow' });
    this.flashVeil = el('div', { className: 'battle-flash' });
    root.prepend(el('div', { className: 'battle-vignette' }), this.tint);
    root.append(this.glow, this.flashVeil);
    // Une fiche par place : celles du HTML pour la place 0, des copies pour les suivantes (combat double).
    for (const selector of ['.side.wild', '.side.mine']) {
      const first = $(selector, root);
      first.dataset.place = '0';
      let previous = first;
      for (let place = 1; place < MAX_FIELD_PLACES; place++) {
        const copy = first.cloneNode(true) as HTMLElement;
        copy.dataset.place = String(place);
        previous.after(copy);
        previous = copy;
      }
    }
    $('.side.wild[data-place="0"] .info', root).append(this.foeTeamRow);
    root.append(el('div', { className: 'danger-vignette' }));
    // Barre « fantôme » derrière chaque jauge de PV : elle rattrape les PV perdus avec un temps de retard.
    for (const slot of ALL_SLOTS) {
      $('.bar.hp', this.side(slot)).append(el('div', { className: 'ghost' }));
      $('.badges', this.side(slot)).append(el('span', { className: 'stages' }));
    }
  }

  /** Places utilisées de chaque camp (1, ou 2 en combat double) : les autres restent cachées. */
  setPlaces(allies: number, foes: number): void {
    this.placeCount = { ally: Math.max(1, allies), foe: Math.max(1, foes) };
    this.root.classList.toggle('double', allies > 1 || foes > 1);
    for (const slot of ALL_SLOTS) this.side(slot).classList.toggle('unused', slot.place >= this.placeCount[slot.target]);
  }

  /** Places utilisées de ce camp. */
  slotsOf(side: BattleSide): Slot[] {
    return Array.from({ length: this.placeCount[side] }, (_, place) => ({ target: side, place }));
  }

  /**
   * Équipe adverse : une capsule par place (au moins six), en couleur pour chaque monstre encore debout, grisée pour
   * les autres ; masquée contre un monstre sauvage (null).
   */
  setFoeTeam(team: TeamCount | null): void {
    this.foeTeam = team;
    this.foeTeamRow.hidden = team === null;
    if (!team) return;
    const slots = Math.max(TEAM_SIZE, team.size);
    this.foeTeamRow.title = `${team.alive} monstre${team.alive > 1 ? 's' : ''} encore en état de combattre sur ${team.size}`;
    this.foeTeamRow.replaceChildren(...Array.from({ length: slots }, (_, index) =>
      icon('item-capsule', { className: index < team.alive ? 'alive' : 'down' })));
  }

  /** Le monstre adverse tombe : sa capsule se grise tout de suite, sans attendre la fin du tour. */
  private foeFainted(): void {
    const team = this.foeTeam;
    if (!team || team.alive <= 0) return;
    this.setFoeTeam({ ...team, alive: team.alive - 1 });
    const capsules = this.foeTeamRow.children;
    const fallen = capsules[team.alive - 1];
    if (fallen) restartAnimation(fallen, 'fallen');
  }

  /** Arène vide : les monstres apparaîtront avec leurs étapes « appear ». */
  reset(): void {
    this.shown.clear();
    this.hp.clear();
    for (const slot of ALL_SLOTS) {
      const element = this.side(slot);
      element.style.visibility = 'hidden';
      element.classList.remove('choosing');
      $('.sprite', element).className = 'sprite';
      this.setStatus(slot, null);
      this.clearStages(slot);
    }
    this.log.replaceChildren();
    this.capsule.hidden = true;
    this.focus(null);
    this.setTint(null);
    for (const particle of this.arena.querySelectorAll('.particle')) particle.remove();
    this.calm();
  }

  /** Met en avant le monstre dont le joueur choisit l'action (null : aucun). */
  highlight(slot: Slot | null): void {
    for (const each of ALL_SLOTS) this.side(each).classList.toggle('choosing', slot !== null && slotKey(each) === slotKey(slot));
  }

  /** Fin du danger : plus de bords rouges ni de battements de cœur (combat fermé…). */
  calm(): void {
    this.root.classList.remove('danger');
    if (this.heartbeat !== null) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /** Météo de la carte (dessinée par le monde) : la lueur du type qu'elle renforce. */
  setWeather(weather: Weather): void {
    this.weather = weather;
  }

  /** Arrêt sur image en cours : le monde, autour de l'arène, se fige lui aussi. */
  get isFrozen(): boolean {
    return this.frozen !== null;
  }

  /** Type du monstre affiché à cette place (null : aucun). */
  elementOf(slot: Slot): Element | null {
    return this.shown.get(slotKey(slot))?.element ?? null;
  }

  /** Monstre affiché à cette place (null : aucun). */
  shownAt(slot: Slot): Displayed | null {
    return this.shown.get(slotKey(slot)) ?? null;
  }

  /** Affiche immédiatement une place dans son état final (fin de séquence). */
  sync(slot: Slot, monster: Displayed | null): void {
    const element = this.side(slot);
    // Place vide, ou monstre tombé (son K.O. a déjà été joué) : plus rien à montrer.
    if (!monster || monster.hp <= 0) {
      element.style.visibility = 'hidden';
      this.shown.delete(slotKey(slot));
      return;
    }
    this.fill(slot, monster, monster.hp);
    element.style.visibility = 'visible';
    const sprite = $('.sprite', element);
    if (monster.hp > 0) sprite.classList.remove('faint', 'absorbed', 'leave');
  }

  // ---------- Étapes animées ----------

  /** Écrit une ligne lettre par lettre, puis laisse `pause` millisecondes pour la lire. */
  async say(text: string, pause: number = TIMING.afterLine): Promise<void> {
    const line = el('p');
    this.log.append(line);
    while (this.log.children.length > MAX_LOG_LINES) this.log.firstElementChild?.remove();
    // Texte écrit lettre par lettre ; en mode accéléré, il s'affiche d'un coup, mais une {pause} se joue jusqu'au bout
    // et l'arrête. Les marqueurs des répliques ({boom}, {pause}, {tremble}…) se jouent quand l'écriture les atteint.
    const typed = new TypedText(text, this.sounds);
    let wait = typed.reach(0);
    for (let written = 0; written < typed.length;) {
      if (this.pacer.isFast) {
        await this.pacer.hold(typed.pauseLeft);
        written = typed.skip();
        wait = 0;
      } else {
        if (wait) await this.pacer.wait(wait);
        written++;
      }
      line.replaceChildren(...typed.render(written));
      if (this.pacer.isFast) continue;
      wait = typed.reach(written);
      await this.pacer.wait(TIMING.typePerChar);
    }
    wait += typed.reach(Infinity);
    await this.pacer.wait(pause + wait);
    await this.pacer.hold(typed.pauseLeft);
  }

  /** Un monstre entre en scène ; l'adversaire arrive en silhouette noire, puis se colore. */
  async appear(slot: Slot, monster: Displayed, hp: number, stages?: Partial<Record<BattleStat, number>>): Promise<void> {
    const side = slot.target;
    this.fill(slot, monster, hp);
    this.clearStages(slot);
    if (stages) {
      this.stages.set(slotKey(slot), stages);
      this.renderStages(slot);
    }
    this.setStatus(slot, null);
    const element = this.side(slot);
    const sprite = $('.sprite', element);
    sprite.classList.remove('faint', 'absorbed', 'leave');
    if (side === 'foe') sprite.classList.add('shadowed');
    element.style.visibility = 'visible';
    playOnce(sprite, side === 'foe' ? 'enter-foe' : 'enter-ally');
    this.groundBurst(slot, monster.element, side === 'foe' ? 0 : TIMING.enter * 0.75);
    restartAnimation($('.info', element), 'info-in');
    try {
      await this.pacer.wait(TIMING.enter);
    } finally {
      sprite.classList.add('reveal');
      sprite.classList.remove('shadowed');
    }
    if (monster.shiny) this.sounds.play('levelup');
    try {
      if (side === 'foe') await this.pacer.wait(TIMING.reveal);
    } finally {
      sprite.classList.remove('reveal');
    }
  }

  /**
   * Gerbe au sol, là où un monstre se pose dans l'arène : une onde de la couleur de son type et des mottes de terre
   * projetées (après `delay` ms : le monstre allié bondit d'abord depuis son dresseur).
   */
  private groundBurst(slot: Slot, element: Element, delay: number): void {
    const burst = el('i', { className: 'ground-burst' }, ...Array.from({ length: 7 }, (_, i) => {
      const clod = el('b');
      clod.style.setProperty('--angle', `${-170 + i * 26 + Math.round(Math.random() * 10)}deg`);
      return clod;
    }));
    burst.style.setProperty('--c', elementTypes.get(element).color);
    burst.style.animationDelay = `${delay}ms`;
    for (const clod of burst.children) (clod as HTMLElement).style.animationDelay = `${delay}ms`;
    burst.addEventListener('animationend', (event) => { if (event.target === burst) burst.remove(); });
    $('.pedestal', this.side(slot)).append(burst);
  }

  /**
   * Le coup porte : arrêt sur image (la cible blanchit, tout se fige), puis la cible encaisse, l'arène tremble
   * d'autant plus que la part des PV perdus est grande, et la jauge tombe (la barre fantôme suit plus tard).
   */
  async damage(slot: Slot, hp: number, effectiveness: number, critical: boolean, weather = false): Promise<void> {
    const side = slot.target;
    const sprite = $('.sprite', this.side(slot));
    const maxHp = this.shownAt(slot)?.maxHp ?? 0;
    const lost = maxHp ? Math.max(0, (this.hp.get(slotKey(slot)) ?? hp) - hp) / maxHp : 0;
    const strong = critical || effectiveness > 1;
    this.sounds.play(effectiveness > 1 ? 'super' : 'hit');
    // La météo renforce le coup : l'arène luit de la couleur du type renforcé.
    if (weather && this.weather.boostedElement) {
      this.glow.style.setProperty('--boost', elementTypes.get(this.weather.boostedElement).color);
      playOnce(this.glow, 'weather-boost');
    }
    // Coup critique : ralenti, zoomé sur la cible.
    if (critical) this.focus(slot);
    try {
      await this.hitStop(sprite, critical ? TIMING.hitStopCritical : strong ? TIMING.hitStopStrong : TIMING.hitStop);
    } finally {
      if (critical) this.focus(null);
    }
    const knockback = strong || lost >= KNOCKBACK_LOSS;
    if (knockback) playOnce(sprite, side === 'foe' ? 'knockback' : 'knockback-ally');
    else playOnce(sprite, side === 'foe' ? 'hit' : 'hit-ally');
    if (strong) playOnce(this.flashVeil, 'flash');
    this.shake(Math.min(SHAKE.max, SHAKE.base + lost * SHAKE.perMaxHp + (strong ? SHAKE.strong : 0)));
    this.setHp(slot, hp);
    await this.pacer.wait(TIMING.hit);
    await this.pacer.wait(TIMING.hpDrain);
  }

  /** Fige l'arène un instant, la cible blanchie : l'impact a du poids. */
  private async hitStop(sprite: HTMLElement, ms: number): Promise<void> {
    let release = () => {};
    this.frozen = new Promise((resolve) => { release = resolve; });
    this.arena.classList.add('hitstop');
    sprite.classList.add('struck');
    try {
      await this.pacer.wait(ms);
    } finally {
      this.arena.classList.remove('hitstop');
      sprite.classList.remove('struck');
      this.frozen = null;
      release();
    }
  }

  /** Les deux camps tremblent, de `px` pixels au plus ; la carte autour aussi (voir BattleField). */
  private shake(px: number): void {
    if (px >= 5) vibrate(Math.round(px * 6));
    this.root.dispatchEvent(new CustomEvent('battle-shake', { detail: px * 0.6 }));
    for (const slot of ALL_SLOTS) {
      const element = this.side(slot);
      element.style.setProperty('--shake', `${px.toFixed(1)}px`);
      restartAnimation(element, 'shake');
    }
  }

  async heal(slot: Slot, hp: number): Promise<void> {
    playOnce($('.sprite', this.side(slot)), 'healed');
    this.sounds.play('heal');
    this.setHp(slot, hp);
    await this.pacer.wait(TIMING.hpDrain);
  }

  /**
   * K.O. : la cible blanchit, l'arène marque un temps, puis elle tombe au ralenti. Le K.O. qui gagne le combat
   * (`final`) a droit au zoom sur le vaincu.
   */
  async faint(slot: Slot, final = false): Promise<void> {
    const sprite = $('.sprite', this.side(slot));
    if (slot.target === 'foe') this.foeFainted();
    this.sounds.play('faint');
    vibrate(final ? [80, 60, 160] : [60, 40, 100]);
    if (final) this.focus(slot);
    playOnce(this.flashVeil, 'flash');
    sprite.classList.add('struck');
    try {
      await this.pacer.wait(TIMING.faintFlash);
    } finally {
      sprite.classList.remove('struck');
    }
    sprite.classList.add('faint');
    await this.pacer.wait(TIMING.faint * (final ? 2 : 1.4));
    if (final) this.focus(null);
  }

  /** Tout ce camp prend la fuite. */
  async flee(side: BattleSide): Promise<void> {
    for (const slot of this.slotsOf(side)) $('.sprite', this.side(slot)).classList.add('leave');
    await this.pacer.wait(TIMING.faint);
  }

  /**
   * Lancer de capsule : elle part en cloche en tournoyant, s'ouvre au-dessus du monstre qui y est aspiré, tombe en
   * rebondissant, tremble `shakes` fois, puis se ferme dans une gerbe d'étincelles ou s'ouvre et le relâche.
   */
  async throwCapsule(place: number, item: ItemId, shakes: number, caught: boolean): Promise<void> {
    const { capsule } = this;
    capsule.dataset.place = String(place);
    const foeSprite = $('.sprite', this.side({ target: 'foe', place }));
    $('.capsule-body', capsule).replaceChildren(itemVisual(itemCatalog.get(item)));
    $('.capsule-fx', capsule).replaceChildren(...Array.from({ length: SPARKS }, (_, i) => {
      const spark = el('span', { className: 'spark' });
      spark.style.setProperty('--angle', `${(360 / SPARKS) * i + (i % 2) * 12}deg`);
      spark.style.setProperty('--delay', `${(i % 3) * 60}ms`);
      return spark;
    }));
    const phase = (name: string) => {
      capsule.className = 'capsule';
      restartAnimation(capsule, name);
    };
    capsule.hidden = false;
    phase('thrown');
    await this.pacer.wait(TIMING.capsuleThrow);

    phase('opening');
    this.sounds.play('select');
    restartAnimation(foeSprite, 'absorbed');
    await this.pacer.wait(TIMING.capsuleOpen);

    phase('landed');
    await this.pacer.wait(TIMING.capsuleDrop);
    for (let i = 0; i < shakes; i++) {
      phase('wobble');
      this.sounds.play('select');
      await this.pacer.wait(TIMING.capsuleShake);
    }

    if (caught) {
      phase('sealed');
      this.sounds.play('capture');
    } else {
      phase('burst');
      foeSprite.classList.remove('absorbed');
      playOnce(foeSprite, 'enter-foe');
      this.sounds.play('hit');
    }
    await this.pacer.wait(TIMING.capsuleResult);
    if (!caught) capsule.hidden = true;
  }

  /**
   * Le monstre de ce camp s'élance pour attaquer (couleur de son type), puis l'effet de l'attaque se joue. Une grosse
   * attaque teinte l'arène à sa couleur ; les plus puissantes zooment d'abord sur le lanceur.
   * Une attaque qui frappe laisse ensuite des particules de son type sur la cible.
   */
  /**
   * `aim` : le monstre visé (null : l'attaque porte sur le lanceur) ; à défaut, le premier monstre d'en face.
   */
  async attack(slot: Slot, element: Element, power: number, animation: MoveAnimation | null, outcome: AttackOutcome = 'hit', aim: Slot | null = null): Promise<void> {
    const side = slot.target;
    const sprite = $('.sprite', this.side(slot));
    const color = elementTypes.get(element).color;
    const victim: Slot = aim ?? { target: opposite(side), place: 0 };
    const target: Slot = animation?.on === 'self' ? slot : victim;
    sprite.style.setProperty('--move-color', color);
    const cinematic = power >= CINEMATIC_POWER;
    if (power >= TINT_POWER) this.setTint(color);
    try {
      if (cinematic) {
        this.focus(slot);
        await this.pacer.wait(TIMING.cinematic);
        this.focus(null);
      }
      playOnce(sprite, side === 'foe' ? 'attack-foe' : 'attack-ally');
      this.sounds.play('select');
      await this.pacer.wait(TIMING.attack);
      if (outcome === 'dodge') {
        // La cible fait un pas de côté, laissant des images rémanentes ; l'attaque passe dans le vide.
        playOnce($('.sprite', this.side(victim)), victim.target === 'foe' ? 'dodge' : 'dodge-ally');
        this.sounds.play('dodge');
        await Promise.all([animation ? this.playEffect(target, animation) : Promise.resolve(), this.pacer.wait(TIMING.dodge)]);
      } else if (outcome === 'immune') {
        // Sans effet : l'animation se joue en gris et se dissout à mi-chemin.
        this.sounds.play('immune');
        if (animation) await this.playEffect(target, animation, true);
      } else if (animation) await this.playEffect(target, animation);
    } finally {
      if (cinematic) this.focus(null);
      this.setTint(null);
    }
    if (power > 0 && outcome === 'hit') this.scatter(victim, element, power);
  }

  /** L'attaque coûte de l'énergie : la jauge se vide sous les yeux. */
  spendEnergy(slot: Slot, energy: number): void {
    this.setEnergy(slot, energy);
    playOnce($('.bar.energy', this.side(slot)), 'drain');
  }

  /** Repos : le monstre respire, la jauge d'énergie se remplit en luisant. */
  async rest(slot: Slot, energy: number): Promise<void> {
    playOnce($('.sprite', this.side(slot)), 'resting');
    this.sounds.play('rest');
    this.setEnergy(slot, energy);
    playOnce($('.bar.energy', this.side(slot)), 'refill');
    await this.pacer.wait(TIMING.rest);
  }

  /** Zoom de l'arène sur le monstre de cette place (null : retour à la normale). */
  private focus(slot: Slot | null): void {
    if (slot) {
      const arena = this.arena.getBoundingClientRect();
      const pedestal = $('.pedestal', this.side(slot)).getBoundingClientRect();
      const x = pedestal.left + pedestal.width / 2 - arena.left, y = pedestal.top + pedestal.height / 2 - arena.top;
      this.arena.style.transformOrigin = `${x}px ${y}px`;
    }
    this.arena.classList.toggle('zoomed', slot !== null);
  }

  /** Voile de cette couleur sur l'écran de combat (null : retiré). */
  private setTint(color: string | null): void {
    if (color) this.tint.style.setProperty('--tint', color);
    this.tint.classList.toggle('on', color !== null);
  }

  /** Particules du type de l'attaque, qui restent quelques secondes autour du monstre touché (plus nombreuses si l'attaque est puissante). */
  private scatter(slot: Slot, element: Element, power: number): void {
    const kind = PARTICLES[element] ?? 'dust';
    const color = elementTypes.get(element).color;
    const pedestal = $('.pedestal', this.side(slot));
    const count = Math.min(18, 5 + Math.round(power / 10));
    for (let i = 0; i < count; i++) {
      const particle = el('i', { className: `particle ${kind}` });
      particle.style.setProperty('--c', color);
      particle.style.setProperty('--x', `${Math.round(15 + Math.random() * 70)}%`);
      particle.style.setProperty('--y', `${Math.round(20 + Math.random() * 60)}%`);
      particle.style.setProperty('--dx', `${Math.round((Math.random() - 0.5) * 60)}px`);
      particle.style.setProperty('--spin', `${Math.round((Math.random() - 0.5) * 540)}deg`);
      particle.style.setProperty('--d', `${(1.6 + Math.random() * 1.6).toFixed(2)}s`);
      particle.style.setProperty('--delay', `${(Math.random() * 0.6).toFixed(2)}s`);
      particle.addEventListener('animationend', () => particle.remove(), { once: true });
      pedestal.append(particle);
    }
  }

  /** Une statistique du monstre de ce camp augmente ou baisse : animation choisie dans l'onglet Configuration. */
  async statChange(slot: Slot, rise: boolean, stat: BattleStat, stages: number): Promise<void> {
    this.stages.set(slotKey(slot), { ...this.stages.get(slotKey(slot)), [stat]: stages });
    this.renderStages(slot);
    const { buffAnimationId, debuffAnimationId } = gameConfig().battle;
    const animationId = rise ? buffAnimationId : debuffAnimationId;
    // Le monstre se redresse (hausse) ou se tasse (baisse), pendant l'animation.
    const sprite = $('.sprite', this.side(slot));
    playOnce(sprite, rise ? 'stat-up' : 'stat-down');
    if (animationId !== null) await this.playEffect(slot, { animationId, on: 'self' });
    else await this.pacer.wait(TIMING.hit);
  }

  private clearStages(slot: Slot): void {
    this.stages.delete(slotKey(slot));
    this.renderStages(slot);
  }

  /** Crans en cours à côté du nom : « Atq ▲2 », « Déf ▼1 »… */
  private renderStages(slot: Slot): void {
    const box = this.side(slot).querySelector('.stages');
    if (!box) return;
    box.replaceChildren(...(Object.entries(this.stages.get(slotKey(slot)) ?? {}) as [BattleStat, number][]).filter(([, value]) => value !== 0).map(([stat, value]) => {
      const chip = el('span', { className: `stage ${value > 0 ? 'up' : 'down'}` }, `${STAGE_LABELS[stat]} ${value > 0 ? '▲' : '▼'}${Math.abs(value)}`);
      chip.title = `${STAGE_LABELS[stat]} ${value > 0 ? '+' : ''}${value}`;
      return chip;
    }));
  }

  /**
   * Animation de l'attaque jouée image par image sur le monstre de ce camp. Les effets sont dessinés pour
   * frapper vers la droite (l'adversaire) : retournés quand ils visent votre monstre. Rend la main à l'image
   * d'impact (la cible encaisse pendant que la suite se joue), ou à la fin s'il n'y en a pas. `fizzle` : l'attaque
   * n'a pas d'effet, l'animation se joue en gris et s'arrête à mi-chemin.
   */
  private async playEffect(slot: Slot, { animationId, on }: MoveAnimation, fizzle = false): Promise<void> {
    const animation = animationCatalog.get(animationId);
    if (!animation) return;
    const sheet = effectSheet(animation);
    if (!sheet.complete) await sheet.decode().catch(() => undefined);
    if (!sheet.naturalWidth) return;
    const fx = el('canvas', { className: 'fx', width: animation.width, height: animation.height });
    fx.style.aspectRatio = `${animation.width} / ${animation.height}`;
    fx.style.width = effectWidth(animation.width, ATTACK_EFFECT_SCALE);
    fx.classList.toggle('mirror', slot.target === 'ally' && on === 'target');
    fx.classList.toggle('fizzle', fizzle);
    const last = fizzle ? Math.ceil(animation.frames.length / 2) : animation.frames.length;
    const impactFrame = fizzle ? null : animation.impactFrame;
    const ctx = fx.getContext('2d');
    $('.pedestal', this.side(slot)).append(fx);
    let impact = () => {};
    const impactReached = new Promise<void>((resolve) => { impact = resolve; });
    const playing = (async () => {
      try {
        for (let frame = 0; frame < last; frame++) {
          ctx?.clearRect(0, 0, fx.width, fx.height);
          if (ctx) drawAnimationFrame(ctx, animation, frame, 0, 0, fx.width, fx.height);
          if (frame === impactFrame) impact();
          await this.pacer.wait(animation.frameMs);
          if (this.frozen) await this.frozen;
        }
      } finally {
        fx.remove();
      }
    })();
    if (impactFrame === null) return playing;
    // La suite de l'animation continue seule ; si la séquence est interrompue, elle s'arrête avec elle.
    playing.catch(() => undefined);
    await Promise.race([impactReached, playing]);
  }


  /** Jauge d'énergie du monstre de cette place (et son chiffre, pour les vôtres). */
  setEnergy(slot: Slot, energy: number): void {
    const element = this.side(slot);
    const fill = $('.bar.energy > div', element);
    fill.style.width = `${(100 * energy) / ENERGY_MAX}%`;
    const text = element.querySelector('.energytext');
    text?.replaceChildren(icon('energy'), ` ${energy}/${ENERGY_MAX}`);
  }

  /**
   * Un statut vient d'être infligé : le monstre se teinte de sa couleur et réagit à sa façon (il frissonne de poison,
   * tressaute de brûlure, grésille de paralysie, s'affaisse de sommeil, se raidit de froid) ; l'animation en boucle
   * prend ensuite le relais.
   */
  async afflict(slot: Slot, status: StatusCondition): Promise<void> {
    this.setStatus(slot, status);
    const sprite = $('.sprite', this.side(slot));
    sprite.style.setProperty('--status-color', STATUSES[status].color);
    sprite.classList.add('afflicted', `afflicted-${status}`);
    try {
      await this.pacer.wait(TIMING.afflict);
    } finally {
      sprite.classList.remove('afflicted', `afflicted-${status}`);
    }
  }

  /** Statut affiché à côté du nom (PSN, BRL, PAR, SOM, GEL), ou retiré. */
  setStatus(slot: Slot, status: StatusCondition | null): void {
    this.statuses.set(slotKey(slot), status);
    const badge = $('.status', this.side(slot));
    badge.hidden = !status;
    if (!status) return;
    badge.textContent = STATUSES[status].short;
    badge.title = STATUSES[status].label;
    badge.style.setProperty('--c', STATUSES[status].color);
  }

  /** Barre d'expérience et niveau d'un monstre allié, d'après ses données à jour ; la barre reste masquée (voir gainXp). */
  private showProgress(slot: Slot, monster: OwnedMonsterView): void {
    const element = this.side(slot);
    $('.bar.xp', element).classList.remove('shown');
    $('.bar.xp > div', element).style.width = `${(100 * monster.xp) / monster.xpNext}%`;
  }

  /** Place alliée où se tient ce monstre (null : il n'est pas sur le terrain). */
  allyOf(monsterId: number): Slot | null {
    return this.slotsOf('ally').find((slot) => {
      const shown = this.shownAt(slot);
      return shown !== null && 'id' in shown && shown.id === monsterId;
    }) ?? null;
  }

  /** Fin des gains d'expérience : les barres s'effacent après un court instant (sans retenir la suite du combat). */
  hideXp(): void {
    clearTimeout(this.xpHideTimer);
    this.xpHideTimer = setTimeout(() => {
      for (const slot of this.slotsOf('ally')) $('.bar.xp', this.side(slot)).classList.remove('shown');
    }, TIMING.xpLinger);
  }

  /** Expérience gagnée : la barre se remplit en cliquetant, jusqu'à celle du monstre (ou jusqu'au bout : `percent`). */
  async gainXp(slot: Slot, monster: OwnedMonsterView, percent?: number): Promise<void> {
    // La barre n'apparaît que le temps de gagner de l'expérience.
    clearTimeout(this.xpHideTimer);
    $('.bar.xp', this.side(slot)).classList.add('shown');
    const fill = $('.bar.xp > div', this.side(slot));
    const from = parseFloat(fill.style.width) || 0;
    const to = percent ?? (100 * monster.xp) / monster.xpNext;
    const duration = Math.min(TIMING.xpFill, Math.max(250, Math.abs(to - from) * 14));
    fill.style.transition = `width ${duration}ms linear`;
    fill.style.width = `${to}%`;
    try {
      for (let elapsed = 0; elapsed < duration; elapsed += TIMING.xpTick) {
        this.sounds.play('tick');
        await this.pacer.wait(TIMING.xpTick);
      }
    } finally {
      fill.style.transition = '';
    }
  }

  /** Montée de niveau : gerbe de lumière sur votre monstre, et « Niveau N ! ». */
  async levelUp(slot: Slot, level: number): Promise<void> {
    const element = this.side(slot);
    $('.lvl', element).textContent = `Nv ${level}`;
    restartAnimation($('.info', element), 'level-up');
    const xp = $('.bar.xp > div', element);
    xp.style.transition = 'none';
    xp.style.width = '0%';
    void xp.offsetWidth;
    xp.style.transition = '';
    const burst = el('div', { className: 'levelup-burst' }, el('span', {}, `Niveau ${level} !`));
    burst.addEventListener('animationend', (event) => { if (event.target === burst) burst.remove(); });
    $('.pedestal', element).append(burst);
    this.sounds.play('levelup');
    await this.pacer.wait(TIMING.hit * 2);
  }

  /** Les pièces gagnées s'envolent vers le compteur du joueur, avec « +N ». */
  async flyCoins(amount: number): Promise<void> {
    const from = this.arena.getBoundingClientRect();
    const counter = document.getElementById('coins')?.getBoundingClientRect();
    const toX = counter && counter.width ? counter.left + counter.width / 2 : innerWidth - 40;
    const toY = counter && counter.height ? counter.top + counter.height / 2 : 30;
    const startX = from.left + from.width / 2, startY = from.top + from.height / 2;
    const gain = el('span', { className: 'coin-gain' }, `+${amount}`);
    gain.style.left = `${startX}px`;
    gain.style.top = `${startY}px`;
    gain.addEventListener('animationend', () => gain.remove(), { once: true });
    this.root.append(gain);
    for (let i = 0; i < Math.min(8, Math.max(3, Math.ceil(amount / 10))); i++) {
      const coin = el('span', { className: 'flying-coin' }, icon('coins'));
      this.root.append(coin);
      const spread = (i - 3.5) * 14;
      coin.animate([
        { transform: `translate(${startX + spread}px, ${startY}px) scale(.6)`, opacity: 0 },
        { transform: `translate(${startX + spread * 1.6}px, ${startY - 50}px) scale(1.1)`, opacity: 1, offset: 0.3 },
        { transform: `translate(${toX}px, ${toY}px) scale(.7)`, opacity: 0.9 },
      ], { duration: 750, delay: i * 60, easing: 'cubic-bezier(.5, 0, .8, .6)', fill: 'both' }).finished.then(() => coin.remove(), () => coin.remove());
    }
    await this.pacer.wait(TIMING.coins);
  }

  /** Le badge apparaît au centre de l'arène en tournoyant. */
  async showBadge(name: IconName, color: string): Promise<void> {
    const badge = el('span', { className: 'arena-badge' }, badgeIcon(name));
    badge.style.setProperty('--c', color);
    this.arena.append(badge);
    this.sounds.play('victory');
    await this.pacer.wait(TIMING.faint * 2);
    badge.remove();
  }

  // ---------- Remplissage ----------

  private side({ target, place }: Slot): HTMLElement {
    return $(`${target === 'foe' ? '.side.wild' : '.side.mine'}[data-place="${place}"]`, this.root);
  }

  private fill(slot: Slot, monster: Displayed, hp: number): void {
    const side = slot.target;
    const element = this.side(slot);
    this.shown.set(slotKey(slot), monster);
    if (!this.painting) {
      this.painting = true;
      requestAnimationFrame(this.paintSprites);
    }
    $('.sparkles', element).hidden = !monster.shiny;
    $('.name', element).replaceChildren(monster.name,
      ...('caught' in monster && monster.caught ? [' ', caughtMark()] : []),
      ...(monster.shiny ? [' ', icon('sparkles', { label: 'Chromatique' })] : []));
    $('.lvl', element).textContent = `Nv ${monster.level}`;
    const badge = $('.element-badge:not(.second)', element);
    const second = $('.element-badge.second', element);
    applyElementBadge(badge, monster.element);
    second.hidden = !monster.secondElement;
    if (monster.secondElement) applyElementBadge(second, monster.secondElement);
    if (side === 'foe') {
      // Au survol du type adverse : ce qui marche bien, ou mal, contre lui (ses deux types réunis).
      const { weak, resisted } = matchupsAgainst(monster);
      const names = (list: { element: Element; multiplier: number }[]) =>
        list.map(({ element: key, multiplier }) => `${elementTypes.get(key).label} ×${Math.round(multiplier * 100) / 100}`).join(', ') || 'aucun';
      const title = `Type ${typesOf(monster).map((key) => elementTypes.get(key).label).join(' / ')}\nSuper efficace contre lui : ${names(weak)}\nPeu efficace contre lui : ${names(resisted)}`;
      badge.title = title;
      second.title = title;
    }
    if ('xpNext' in monster) this.showProgress(slot, monster);
    this.setHp(slot, hp, true);
  }

  /**
   * Dessine les deux monstres : l'adversaire de face, celui du joueur de dos (retourné vers l'adversaire s'il n'a pas
   * d'animation de dos). Continue à chaque image tant qu'un monstre est affiché (en attendant plus calmement quand
   * l'écran de combat est caché).
   */
  private readonly paintSprites = (now: number): void => {
    let visible = false;
    for (const slot of ALL_SLOTS) {
      if (this.frozen) {
        visible = true;
        continue;
      }
      const monster = this.shownAt(slot);
      if (!monster) continue;
      const canvas = $<HTMLCanvasElement>('.sprite', this.side(slot));
      const frame = paintSpriteCanvas(canvas, monster, slot.target === 'ally' ? 'back' : 'front', now);
      canvas.classList.toggle('back', frame?.back ?? false);
      visible ||= canvas.clientWidth > 0;
      this.paintStatus(slot, canvas, now);
    }
    if (!this.shown.size) this.painting = false;
    else if (visible) requestAnimationFrame(this.paintSprites);
    else setTimeout(() => requestAnimationFrame(this.paintSprites), HIDDEN_REPAINT_MS);
  };

  /** Animation du statut de ce camp, jouée en boucle sur le monstre tant qu'il est là (retirée sinon). */
  private paintStatus(slot: Slot, sprite: HTMLElement, now: number): void {
    const pedestal = $('.pedestal', this.side(slot));
    let fx = pedestal.querySelector<HTMLCanvasElement>('.fx.status-fx');
    const status = this.statuses.get(slotKey(slot)) ?? null;
    const animationId = status && gameConfig().battle.statusAnimations[status];
    const animation = animationId ? animationCatalog.get(animationId) : undefined;
    const away = sprite.classList.contains('faint') || sprite.classList.contains('absorbed') || sprite.classList.contains('leave');
    if (!animation?.frames.length || away) {
      fx?.remove();
      return;
    }
    if (!fx) {
      fx = el('canvas', { className: 'fx status-fx' });
      pedestal.prepend(fx);
    }
    if (fx.width !== animation.width || fx.height !== animation.height) {
      fx.width = animation.width;
      fx.height = animation.height;
      fx.style.aspectRatio = `${animation.width} / ${animation.height}`;
      fx.style.width = effectWidth(animation.width);
    }
    const ctx = fx.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, fx.width, fx.height);
    drawAnimationFrame(ctx, animation, Math.floor(now / animation.frameMs) % animation.frames.length, 0, 0, fx.width, fx.height);
  }

  /** Jauge de PV ; la barre fantôme la rattrape avec retard, sauf `instant` (nouveau monstre affiché). */
  private setHp(slot: Slot, hp: number, instant = false): void {
    const monster = this.shownAt(slot);
    if (!monster) return;
    this.hp.set(slotKey(slot), hp);
    const element = this.side(slot);
    const bar = $('.bar.hp', element);
    setHpBar(bar, hp, monster.maxHp);
    const ghost = bar.querySelector<HTMLElement>('.ghost');
    if (ghost) {
      if (instant) ghost.style.transition = 'none';
      ghost.style.width = `${monster.maxHp ? (100 * hp) / monster.maxHp : 0}%`;
      if (instant) {
        void ghost.offsetWidth;
        ghost.style.transition = '';
      }
    }
    const text = element.querySelector('.hptext');
    if (text) text.textContent = `${hp} / ${monster.maxHp} PV`;
    if (slot.target === 'ally') this.updateDanger();
  }

  /** Un de vos monstres passe sous DANGER_HP de ses PV : jauge qui clignote, bords rouges et battements de cœur. */
  private updateDanger(): void {
    const danger = this.slotsOf('ally').some((slot) => {
      const monster = this.shownAt(slot);
      const hp = this.hp.get(slotKey(slot)) ?? 0;
      return monster !== null && hp > 0 && monster.maxHp > 0 && hp / monster.maxHp <= DANGER_HP;
    });
    for (const slot of this.slotsOf('ally')) {
      const monster = this.shownAt(slot);
      const hp = this.hp.get(slotKey(slot)) ?? 0;
      this.side(slot).classList.toggle('in-danger', monster !== null && hp > 0 && hp / monster.maxHp <= DANGER_HP);
    }
    if (!danger) return this.calm();
    this.root.classList.add('danger');
    if (this.heartbeat !== null) return;
    this.sounds.play('heartbeat');
    this.heartbeat = setInterval(() => {
      if (this.root.hidden) this.calm();
      else this.sounds.play('heartbeat');
    }, HEARTBEAT_MS);
  }
}
